import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Empty, Input, Modal, Popconfirm, Radio, Space, Switch, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  CopyOutlined, DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { Table, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { notificationController } from '@app/controllers/notificationController';
import {
  createUnreleasedFeature, deleteUnreleasedFeature, getAppBuilds, getAppReleases, getDeployments,
  getUnreleasedFeatures, updateAppReleaseSummary, updateDeploymentSummary, updateUnreleasedFeature,
} from '@app/api/admin.api';
import { buildSummary, printSummary } from './summary';
import {
  APP_COLORS, APP_LABELS, App, AppBuild, AppRelease, Deployment, PLATFORM_LABELS, Platform,
  TARGET_LABELS, UnreleasedFeature,
} from './types';

const { Text } = Typography;

const fmt = (iso: string | null, withTime = false) =>
  iso ? dayjs(iso).format(withTime ? 'MMM D, YYYY h:mm A' : 'MMM D, YYYY') : '—';

// Store release dates in UTC, matching what the App Store shows.
const storeDate = (iso: string | null) => (iso ? dayjs(iso.slice(0, 10)).format('MMM D, YYYY') : '—');

const Lines: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.split('\n').map((l) => l.replace(/^[-•]\s*/, '').trim()).filter(Boolean);
  if (!lines.length) return <Text type="secondary">No summary yet</Text>;
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {lines.map((l, i) => <li key={i}>{l}</li>)}
    </ul>
  );
};

const AppTag: React.FC<{ app: App }> = ({ app }) => (
  <Tag color={APP_COLORS[app]} style={{ marginRight: 0 }}>{APP_LABELS[app]}</Tag>
);

/** A branch build or deploy is information, not an error: prod runs feature/wifi on purpose. */
const Branch: React.FC<{ branch: string; onMain: boolean | null; warnIfOff?: boolean }> = ({
  branch, onMain, warnIfOff,
}) => {
  if (!branch && onMain === null) return <Text type="secondary">—</Text>;
  if (onMain) return <Tag>main</Tag>;
  return (
    <Tooltip title="Not on main: may include unreleased work">
      <Tag color={warnIfOff ? 'orange' : 'blue'}>{branch || 'other branch'}</Tag>
    </Tooltip>
  );
};

const Commit: React.FC<{ sha: string }> = ({ sha }) =>
  sha ? <Text code>{sha.slice(0, 7)}</Text> : <Text type="secondary">—</Text>;

const LiveTag: React.FC = () => <Tag color="green" style={{ marginLeft: 6 }}>Live</Tag>;

const BUILD_STATUS: Record<AppBuild['status'], { label: string; color: string }> = {
  live: { label: 'Live', color: 'green' },
  released: { label: 'Released', color: 'blue' },
  testing: { label: 'In testing', color: 'gold' },
  superseded: { label: 'Not released', color: 'default' },
};

type AppFilter = 'all' | App;
type PlatformFilter = 'all' | Platform;

const ReleasesPage: React.FC = () => {
  const [releases, setReleases] = useState<AppRelease[]>([]);
  const [builds, setBuilds] = useState<AppBuild[]>([]);
  const [unreleased, setUnreleased] = useState<UnreleasedFeature[]>([]);
  const [adminDeploys, setAdminDeploys] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(false);
  const [appFilter, setAppFilter] = useState<AppFilter>('all');
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');

  const [deploys, setDeploys] = useState<Deployment[]>([]);
  const [deployCount, setDeployCount] = useState(0);
  const [deployPage, setDeployPage] = useState(1);
  const [deploySize, setDeploySize] = useState(DEFAULT_PAGE_SIZE);
  const [target, setTarget] = useState<'all' | 'backend' | 'admin_panel'>('all');
  const [loadingDeploys, setLoadingDeploys] = useState(false);

  const [editing, setEditing] = useState<{ kind: 'release' | 'deploy'; id: number; title: string; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getAppReleases(), getAppBuilds(), getUnreleasedFeatures(),
      getDeployments({ target: 'admin_panel', page_size: 10 }),
    ])
      .then(([r, b, u, a]) => {
        setReleases(r);
        setBuilds(b);
        setUnreleased(u);
        setAdminDeploys(a.results || []);
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => setLoading(false));
  }, []);

  const loadDeploys = useCallback((page = 1, size = DEFAULT_PAGE_SIZE) => {
    setLoadingDeploys(true);
    getDeployments({ page, page_size: size, target: target === 'all' ? undefined : target })
      .then((data) => {
        setDeploys(data.results || []);
        setDeployCount(data.count || 0);
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => setLoadingDeploys(false));
  }, [target]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setDeployPage(1);
    loadDeploys(1, deploySize);
    // Reload on filter change only; paging reloads itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const refresh = () => {
    load();
    loadDeploys(deployPage, deploySize);
  };

  const summary = useMemo(
    () => buildSummary(releases, builds, adminDeploys, unreleased),
    [releases, builds, adminDeploys, unreleased],
  );

  const copySummary = () =>
    navigator.clipboard.writeText(summary)
      .then(() => message.success('Summary copied, ready to paste in Slack'))
      .catch(() => notificationController.error({ message: 'Could not copy to the clipboard' }));

  const current = useMemo(() => {
    const tiles: { key: string; app: App; platform: Platform; release?: AppRelease; testing?: AppBuild }[] = [];
    (['ease', 'wavon'] as App[]).forEach((app) =>
      (['ios', 'android'] as Platform[]).forEach((platform) => {
        const release = releases.find((r) => r.app === app && r.platform === platform && r.is_live);
        const testing = builds.find((b) => b.app === app && b.platform === platform && b.status === 'testing');
        tiles.push({ key: `${app}-${platform}`, app, platform, release, testing });
      }));
    return tiles;
  }, [releases, builds]);

  const byFilters = <T extends { app: App; platform: Platform }>(rows: T[]) =>
    rows.filter((r) => (appFilter === 'all' || r.app === appFilter)
      && (platformFilter === 'all' || r.platform === platformFilter));

  const saveSummary = () => {
    if (!editing) return;
    setSaving(true);
    const request = editing.kind === 'release'
      ? updateAppReleaseSummary(editing.id, editing.text)
      : updateDeploymentSummary(editing.id, editing.text);
    request
      .then(() => {
        setEditing(null);
        refresh();
      })
      .catch((e) => notificationController.error({ message: e.message }))
      .finally(() => setSaving(false));
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    createUnreleasedFeature(newFeature.trim())
      .then(() => {
        setNewFeature('');
        load();
      })
      .catch((e) => notificationController.error({ message: e?.response?.data?.title?.[0] || e.message }));
  };

  const releaseColumns: ColumnsType<AppRelease> = [
    { title: 'App', dataIndex: 'app', width: 90, render: (v: App) => <AppTag app={v} /> },
    { title: 'Platform', dataIndex: 'platform', width: 100, render: (v: Platform) => PLATFORM_LABELS[v] },
    {
      title: 'Version',
      dataIndex: 'version',
      width: 120,
      render: (v: string, r) => <span><b>{v}</b>{r.is_live && <LiveTag />}</span>,
    },
    {
      title: 'Released',
      dataIndex: 'released_at',
      width: 150,
      render: (v: string | null, r) =>
        r.date_confirmed ? storeDate(v) : (
          <Tooltip title="Estimated from git; Google Play doesn't publish version history">
            <Text type="secondary">~{storeDate(v)}</Text>
          </Tooltip>
        ),
    },
    {
      title: 'Build',
      dataIndex: 'build_number',
      width: 120,
      render: (v: string, r) =>
        !v ? <Text type="secondary">—</Text> : r.build_confirmed ? v : (
          <Tooltip title="Assumed: the newest build of this version before release. The stores don't say which build went live.">
            <span>{v} <Text type="secondary" style={{ fontSize: 12 }}>assumed</Text></span>
          </Tooltip>
        ),
    },
    {
      title: "What's new",
      dataIndex: 'summary',
      ellipsis: true,
      render: (v: string, r) => (v || r.store_notes).split('\n').filter(Boolean)[0] || <Text type="secondary">—</Text>,
    },
    {
      title: '',
      width: 90,
      align: 'right',
      render: (_: unknown, r) => (
        <Button
          size="small"
          type="text"
          className="row-action"
          icon={<EditOutlined />}
          onClick={() => setEditing({
            kind: 'release',
            id: r.id,
            title: `${APP_LABELS[r.app]} ${PLATFORM_LABELS[r.platform]} ${r.version}`,
            text: r.summary,
          })}
        >
          Edit
        </Button>
      ),
    },
  ];

  const buildColumns: ColumnsType<AppBuild> = [
    { title: 'App', dataIndex: 'app', width: 90, render: (v: App) => <AppTag app={v} /> },
    { title: 'Platform', dataIndex: 'platform', width: 100, render: (v: Platform) => PLATFORM_LABELS[v] },
    { title: 'Version (build)', width: 130, render: (_: unknown, b) => <span><b>{b.version}</b> ({b.build_number})</span> },
    { title: 'Uploaded', dataIndex: 'built_at', width: 190, render: (v: string | null) => fmt(v, true) },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (v: AppBuild['status']) => <Tag color={BUILD_STATUS[v].color}>{BUILD_STATUS[v].label}</Tag>,
    },
    // Orange here: an app build from a branch can ship unreleased features to customers.
    { title: 'Branch', width: 150, render: (_: unknown, b) => <Branch branch={b.branch} onMain={b.on_main} warnIfOff /> },
    { title: 'Commit', dataIndex: 'commit_sha', width: 100, render: (v: string) => <Commit sha={v} /> },
    { title: 'Workflow', dataIndex: 'workflow', ellipsis: true, render: (v: string) => v || '—' },
  ];

  const deployColumns: ColumnsType<Deployment> = [
    {
      title: 'Where',
      dataIndex: 'target',
      width: 160,
      render: (v: Deployment['target'], d) => <span>{TARGET_LABELS[v]}{d.is_live && <LiveTag />}</span>,
    },
    { title: 'Deployed', dataIndex: 'deployed_at', width: 190, render: (v: string) => fmt(v, true) },
    {
      title: 'Release',
      dataIndex: 'external_id',
      width: 90,
      render: (v: string, d) => (d.target === 'backend' ? v : '—'),
    },
    { title: 'Branch', width: 140, render: (_: unknown, d) => <Branch branch={d.branch} onMain={d.on_main} /> },
    { title: 'Commit', dataIndex: 'commit_sha', width: 100, render: (v: string) => <Commit sha={v} /> },
    {
      title: 'Changes',
      dataIndex: 'summary',
      ellipsis: true,
      render: (v: string) => {
        const lines = v.split('\n').filter(Boolean);
        return lines.length
          ? <span>{lines[0]}{lines.length > 1 && <Text type="secondary"> +{lines.length - 1} more</Text>}</span>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: '',
      width: 90,
      align: 'right',
      render: (_: unknown, d) => (
        <Button
          size="small"
          type="text"
          className="row-action"
          icon={<EditOutlined />}
          onClick={() => setEditing({
            kind: 'deploy',
            id: d.id,
            title: `${TARGET_LABELS[d.target]} deploy, ${fmt(d.deployed_at)}`,
            text: d.summary,
          })}
        >
          Edit
        </Button>
      ),
    },
  ];

  const filters = (
    <Space>
      <Radio.Group
        optionType="button"
        buttonStyle="solid"
        value={appFilter}
        onChange={(e) => setAppFilter(e.target.value as AppFilter)}
        options={[{ label: 'All apps', value: 'all' }, { label: 'Ease', value: 'ease' }, { label: 'Wavon', value: 'wavon' }]}
      />
      <Radio.Group
        optionType="button"
        buttonStyle="solid"
        value={platformFilter}
        onChange={(e) => setPlatformFilter(e.target.value as PlatformFilter)}
        options={[{ label: 'All', value: 'all' }, { label: 'iPhone', value: 'ios' }, { label: 'Android', value: 'android' }]}
      />
    </Space>
  );

  return (
    <>
      <Toolbar>
        <PageTitle>Releases</PageTitle>
        <ToolbarActions>
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
          <Button icon={<CopyOutlined />} onClick={copySummary} disabled={!releases.length}>
            Copy summary for Slack
          </Button>
          <Button type="primary" icon={<FilePdfOutlined />} onClick={() => printSummary(summary)} disabled={!releases.length}>
            Download PDF
          </Button>
        </ToolbarActions>
      </Toolbar>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {current.map(({ key, app, platform, release, testing }) => (
          <div
            key={key}
            style={{
              flex: 1, minWidth: 170, padding: '12px 14px', borderRadius: 10,
              background: 'var(--soft-panel-bg)', border: 'var(--soft-panel-border)',
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.65 }}>{APP_LABELS[app]} · {PLATFORM_LABELS[platform]}</div>
            <div style={{ fontSize: 21, fontWeight: 600, color: APP_COLORS[app] }}>
              {release ? release.version : '—'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.65 }}>
              {release ? `Live since ${storeDate(release.released_at)}` : 'Nothing recorded'}
            </div>
            {testing && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <Tag color={testing.on_main === false ? 'orange' : 'gold'} style={{ marginRight: 0 }}>
                  {testing.version} ({testing.build_number}) in testing
                  {testing.on_main === false ? ` · ${testing.branch}` : ''}
                </Tag>
              </div>
            )}
          </div>
        ))}
      </div>

      <CommonCard id="releases" padding="0.5rem 0.75rem 0">
        <Tabs defaultActiveKey="releases">
          <Tabs.TabPane tab="App releases" key="releases">
            <div style={{ marginBottom: 10 }}>{filters}</div>
            <Table
              rowKey="id"
              columns={releaseColumns}
              dataSource={byFilters(releases)}
              loading={loading}
              expandable={{
                expandedRowRender: (r: AppRelease) => (
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div><Text strong>What&apos;s new</Text><Lines text={r.summary} /></div>
                    {r.store_notes && (
                      <div><Text strong>Published store notes</Text><Lines text={r.store_notes} /></div>
                    )}
                    {(r.commit_sha || r.branch) && (
                      <Text type="secondary">Built from <Commit sha={r.commit_sha} /> {r.branch && `on ${r.branch}`}</Text>
                    )}
                  </Space>
                ),
              }}
              locale={{ emptyText: <Empty description="No releases recorded yet" /> }}
              pagination={{ defaultPageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: PAGE_SIZE_OPTIONS }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Builds" key="builds">
            <div style={{ marginBottom: 10 }}>{filters}</div>
            <Table
              rowKey="id"
              columns={buildColumns}
              dataSource={byFilters(builds)}
              loading={loading}
              locale={{ emptyText: <Empty description="No builds recorded yet" /> }}
              pagination={{ defaultPageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: PAGE_SIZE_OPTIONS }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Server & admin deploys" key="deploys">
            <div style={{ marginBottom: 10 }}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={target}
                onChange={(e) => setTarget(e.target.value as typeof target)}
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Server', value: 'backend' },
                  { label: 'Admin panel', value: 'admin_panel' },
                ]}
              />
            </div>
            <Table
              rowKey="id"
              columns={deployColumns}
              dataSource={deploys}
              loading={loadingDeploys}
              expandable={{
                expandedRowRender: (d: Deployment) => <Lines text={d.summary} />,
                rowExpandable: (d: Deployment) => !!d.summary,
              }}
              locale={{ emptyText: <Empty description="No deploys recorded yet" /> }}
              pagination={{
                current: deployPage,
                pageSize: deploySize,
                total: deployCount,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: (p, size) => {
                  const next = size || deploySize;
                  setDeployPage(p);
                  setDeploySize(next);
                  loadDeploys(p, next);
                },
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Not available yet" key="unreleased">
            <Text type="secondary">
              Built or in progress, but not available to customers. Listed under &quot;Please don&apos;t
              mention yet&quot; in the shared summary.
            </Text>
            <Space style={{ margin: '12px 0', width: '100%' }}>
              <Input
                style={{ width: 360 }}
                placeholder="e.g. Apple Watch app"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onPressEnter={addFeature}
              />
              <Button icon={<PlusOutlined />} onClick={addFeature}>Add</Button>
            </Space>
            <Table
              rowKey="id"
              dataSource={unreleased}
              loading={loading}
              pagination={false}
              columns={[
                { title: 'Feature', dataIndex: 'title' },
                {
                  title: 'In summary',
                  dataIndex: 'is_active',
                  width: 120,
                  render: (v: boolean, f: UnreleasedFeature) => (
                    <Switch
                      size="small"
                      checked={v}
                      onChange={(checked) => updateUnreleasedFeature(f.id, checked).then(load)}
                    />
                  ),
                },
                {
                  title: '',
                  width: 100,
                  align: 'right',
                  render: (_: unknown, f: UnreleasedFeature) => (
                    <Popconfirm
                      title="Remove from the list? Turn it off instead to keep it for later."
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteUnreleasedFeature(f.id).then(load)}
                    >
                      <Button size="small" danger type="text" icon={<DeleteOutlined />} className="row-action">
                        Remove
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]}
            />
          </Tabs.TabPane>
        </Tabs>
      </CommonCard>

      <Modal
        title={editing ? `What's new: ${editing.title}` : ''}
        visible={!!editing}
        onCancel={() => setEditing(null)}
        onOk={saveSummary}
        okText="Save"
        confirmLoading={saving}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">One change per line, in customer language. Used in the shared summary.</Text>
          <Input.TextArea
            rows={8}
            value={editing?.text}
            onChange={(e) => editing && setEditing({ ...editing, text: e.target.value })}
          />
        </Space>
      </Modal>
    </>
  );
};

export default ReleasesPage;
